import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, isDay, Kpi, plain, Table } from './Reports.tsx';
import { AnalyticTab } from './Analytic.tsx';
import { CashFlowStatement, PartnerLedger } from './CashFlow.tsx';
import { DeferralsTab } from './Deferrals.tsx';
import { RevaluationTab } from './Revaluation.tsx';
import { VatBooksTab } from './VatBooks.tsx';
import { YearEndTab } from './YearEnd.tsx';

type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';
type CashFlowKind = 'cash' | 'operating' | 'investing' | 'financing';
const CASH_FLOWS: CashFlowKind[] = ['cash', 'operating', 'investing', 'financing'];
type Journal = 'sales' | 'purchases' | 'cash' | 'bank' | 'stock' | 'general';
interface AccountRow {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  key: string | null;
  active: boolean;
  /** Where its money goes in the cash flow statement, and whether that was chosen on the account. */
  cash_flow?: CashFlowKind;
  cash_flow_set?: boolean;
  used: boolean;
  opening: number;
  debit: number;
  credit: number;
  movement: number;
  closing: number;
}
interface Books {
  from: string;
  to: string;
  accounts: AccountRow[];
  profit_loss: { income: number; expenses: number; profit: number };
  balance_sheet: { assets: number; liabilities: number; equity: number; earnings: number };
  vat: { on_sales: number; on_purchases: number; due: number };
  totals: { debit: number; credit: number };
}
interface Overview {
  today: string;
  lock_date: string | null;
  entries: number;
  last_date: string | null;
  missing: Record<'sales' | 'cash' | 'shifts' | 'bills' | 'payments' | 'stock' | 'gift_cards' | 'tips' | 'invoices' | 'receipts' | 'payroll' | 'payroll_paid' | 'assets' | 'landed' | 'dropship' | 'commissions', number>;
}
interface EntryRow {
  id: string;
  number: number;
  date: string;
  journal: Journal;
  reference: string | null;
  memo: string;
  total: number;
  manual: boolean;
  reversal_of: string | null;
  reversed_by: number | null;
  by_name: string | null;
}
interface EntryLine {
  id: string;
  account: string;
  code: string;
  account_name: string;
  debit: number;
  credit: number;
  label: string | null;
  partner: string | null;
}
interface EntryDetail {
  entry: EntryRow & { created_at: string; reverses: { id: string; number: number } | null; reversed_by: { id: string; number: number } | null };
  lines: EntryLine[];
}
interface Ledger {
  account: AccountRow;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
  more: boolean;
  lines: { id: string; entry: string; number: number; date: string; journal: Journal; reference: string | null; memo: string; label: string | null; partner: string | null; debit: number; credit: number; balance: number }[];
}

type Preset = 'month' | 'last_month' | 'year' | 'last_year' | 'custom';
type Tab = 'overview' | 'journal' | 'accounts' | 'statements' | 'deferrals' | 'analytic' | 'vat' | 'fx' | 'year';
type Statement = 'pl' | 'bs' | 'cf' | 'partners' | 'vat' | 'tb';
const STATEMENTS: Statement[] = ['pl', 'bs', 'cf', 'partners', 'vat', 'tb'];
const PRESETS: Preset[] = ['month', 'last_month', 'year', 'last_year', 'custom'];
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'journal', icon: 'list' },
  { key: 'accounts', icon: 'book' },
  { key: 'statements', icon: 'receipt' },
  { key: 'deferrals', icon: 'calendar' },
  { key: 'analytic', icon: 'layers' },
  { key: 'vat', icon: 'percent' },
  { key: 'fx', icon: 'globe' },
  { key: 'year', icon: 'calendar' },
];
const TYPES: AccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];
const JOURNALS: Journal[] = ['sales', 'purchases', 'cash', 'bank', 'stock', 'general'];
const JOURNAL_LOOK: Record<Journal, { tone: Tone; icon: IconName }> = {
  sales: { tone: 'ok', icon: 'receipt' },
  purchases: { tone: 'warn', icon: 'bag' },
  cash: { tone: 'primary', icon: 'cash' },
  bank: { tone: 'info', icon: 'bank' },
  stock: { tone: 'neutral', icon: 'list' },
  general: { tone: 'neutral', icon: 'edit' },
};

function presetRange(p: Exclude<Preset, 'custom'>, today: string) {
  const y = Number(today.slice(0, 4));
  const month = today.slice(0, 8);
  switch (p) {
    case 'month':
      return { from: `${month}01`, to: today };
    case 'last_month': {
      const end = new Date(`${month}01T00:00:00Z`);
      end.setUTCDate(0);
      const e = end.toISOString().slice(0, 10);
      return { from: `${e.slice(0, 8)}01`, to: e };
    }
    case 'year':
      return { from: `${y}-01-01`, to: today };
    case 'last_year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  }
}

/** Positive amounts plain, negative ones with a minus; zero as a dash so tables stay readable. */
const amount = (n: number) => (n === 0 ? '—' : money(n));
const side = (n: number) => (n ? money(n) : '');

export function Accounting() {
  const today = todayIso();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const preset = (PRESETS.includes(params.get('p') as Preset) ? params.get('p') : 'month') as Preset;
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const custom = { from: isDay(params.get('from')) ? params.get('from')! : `${today.slice(0, 8)}01`, to: isDay(params.get('to')) ? params.get('to')! : today };
  const range = preset === 'custom' ? custom : presetRange(preset, today);
  const valid = range.from <= range.to;
  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((v) => v + 1);
  const { data: overview, reload } = useAction<Overview>('accounting.overview', { v: version });
  const { data: books } = useAction<Books>('accounting.reports', valid ? { from: range.from, to: range.to, v: version } : { from: today, to: today });
  const [entryId, setEntryId] = useState<string | null>(null);
  const [ledgerOf, setLedgerOf] = useState<AccountRow | null>(null);
  const [newEntry, setNewEntry] = useState(false);
  const [closing, setClosing] = useState(false);

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  const current = books && books.from === range.from && books.to === range.to ? books : null;
  const changed = () => {
    refresh();
    void reload();
  };

  return (
    <Shell>
      <div className="page reports accounting">
        <div className="page-head">
          <div>
            <h1>{t('acc.title')}</h1>
            <p className="muted">{range.from === range.to ? day(range.from) : t('rep.range', { from: day(range.from), to: day(range.to) })}</p>
          </div>
          <div className="page-actions">
            {overview?.lock_date ? (
              <span className="badge badge-neutral" title={t('acc.closed_hint')}>
                <Icon name="lock" size={14} />
                <span>{t('acc.closed_to', { date: day(overview.lock_date) })}</span>
              </span>
            ) : null}
            {can('accounting.settings') ? (
              <Button variant="secondary" icon="lock" onClick={() => setClosing(true)}>
                {t('acc.close_books')}
              </Button>
            ) : null}
            {can('accounting.post') ? (
              <Button variant="primary" icon="plus" onClick={() => setNewEntry(true)}>
                {t('acc.new_entry')}
              </Button>
            ) : null}
          </div>
        </div>

        {overview ? <CatchUp overview={overview} onDone={changed} /> : null}

        <div className="toolbar" hidden={tab === 'vat' || tab === 'fx' || tab === 'year'}>
          <div className="toolbar-group">
            <div className="segmented" role="radiogroup" aria-label={t('rep.period')}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  role="radio"
                  aria-checked={preset === p}
                  className={preset === p ? 'active' : ''}
                  onClick={() => set(p === 'custom' ? { p, from: range.from, to: range.to } : { p: p === 'month' ? null : p, from: null, to: null })}
                >
                  {t(`acc.p_${p}`)}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div className="rep-dates">
                <input className="input date-input" type="date" aria-label={t('rep.from')} value={range.from} onChange={(e) => isDay(e.target.value) && set({ from: e.target.value })} />
                <span className="muted" aria-hidden="true">
                  –
                </span>
                <input className="input date-input" type="date" aria-label={t('rep.to')} value={range.to} onChange={(e) => isDay(e.target.value) && set({ to: e.target.value })} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="segmented tabs" role="tablist" aria-label={t('acc.title')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`acc.tab_${x.key}`)}
            </button>
          ))}
        </div>

        {tab === 'year' ? (
          <YearEndTab version={version} onClosed={changed} onOpenEntry={setEntryId} onBalances={(y) => set({ tab: 'statements', s: 'bs', p: 'custom', from: y.start, to: y.end })} />
        ) : tab === 'vat' ? (
          <VatBooksTab initialMonth={(valid ? range.to : today).slice(0, 7)} version={version} />
        ) : tab === 'fx' ? (
          <RevaluationTab initialDate={valid ? range.to : today} version={version} onPosted={changed} onOpenEntry={setEntryId} />
        ) : !valid ? (
          <EmptyState icon="alert" title={t('rep.bad_range')} body={t('rep.bad_range_body')} />
        ) : tab === 'journal' ? (
          <JournalTab from={range.from} to={range.to} version={version} onOpen={setEntryId} />
        ) : !current ? (
          <div className="rep-grid">
            <Skeleton h={112} r="var(--radius-lg)" />
            <Skeleton h={300} r="var(--radius-lg)" />
          </div>
        ) : tab === 'deferrals' ? (
          <DeferralsTab accounts={current.accounts} version={version} onEntry={setEntryId} onChanged={changed} />
        ) : tab === 'analytic' ? (
          <AnalyticTab from={range.from} to={range.to} version={version} accounts={current.accounts} onOpenEntry={setEntryId} />
        ) : tab === 'accounts' ? (
          <AccountsTab books={current} onOpen={setLedgerOf} onChanged={changed} />
        ) : tab === 'statements' ? (
          <Statements
            books={current}
            version={version}
            which={(STATEMENTS.includes(params.get('s') as Statement) ? params.get('s') : 'pl') as Statement}
            onWhich={(s) => set({ s: s === 'pl' ? null : s })}
            onOpen={setLedgerOf}
            onEntry={setEntryId}
          />
        ) : (
          <OverviewTab books={current} onOpen={setLedgerOf} onTab={(k, s) => set({ tab: k, s: s ?? null })} />
        )}
      </div>
      {entryId ? <EntryDialog id={entryId} onClose={() => setEntryId(null)} onOpen={setEntryId} onChanged={changed} /> : null}
      {ledgerOf ? <LedgerDialog account={ledgerOf} from={range.from} to={range.to} onClose={() => setLedgerOf(null)} onEntry={(id) => setEntryId(id)} /> : null}
      {newEntry && current ? (
        <EntryForm
          accounts={current.accounts}
          lockDate={overview?.lock_date ?? null}
          onClose={() => setNewEntry(false)}
          onDone={(id) => {
            setNewEntry(false);
            changed();
            setEntryId(id);
          }}
        />
      ) : null}
      {closing && overview ? <CloseBooks current={overview.lock_date} onClose={() => setClosing(false)} onDone={() => (setClosing(false), changed())} /> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Catching up */

function CatchUp({ overview, onDone }: { overview: Overview; onDone: () => void }) {
  const { can } = useSession();
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState(false);
  const total = Object.values(overview.missing).reduce((a, b) => a + b, 0);
  if (!total) return null;
  const run = async () => {
    setBusy(true);
    try {
      let posted = 0;
      // Large histories go in rounds, so each round stays short.
      for (;;) {
        const r = await action<{ posted: number; left: number }>('accounting.catch_up', {});
        posted += r.posted;
        if (!r.left || !r.posted) break;
      }
      toast(tn('acc.caught_up', posted), 'ok');
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="callout callout-warn acc-catchup" role="status">
      <Icon name="alert" size={20} />
      <div>
        <p className="strong">{tn('acc.missing', total)}</p>
        <p className="muted">{t('acc.missing_hint')}</p>
      </div>
      {can('accounting.post') ? (
        <Button variant="primary" icon="check" busy={busy} onClick={run}>
          {t('acc.post_missing')}
        </Button>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- Overview */

const byKey = (b: Books, key: string) => b.accounts.find((a) => a.key === key);

function OverviewTab({ books: b, onOpen, onTab }: { books: Books; onOpen: (a: AccountRow) => void; onTab: (t: Tab, s?: Statement) => void }) {
  const pl = b.profit_loss;
  const money_ = ['registers', 'office_cash', 'bank', 'card_clearing', 'other_receivable'].map((k) => byKey(b, k)).filter((a): a is AccountRow => !!a);
  const owed = ['payable', 'goods_not_billed', 'tips', 'vat_out'].map((k) => byKey(b, k)).filter((a): a is AccountRow => !!a);
  const extraOwed = b.accounts.filter((a) => a.type === 'liability' && !a.key && a.closing !== 0);
  const expenses = b.accounts.filter((a) => a.type === 'expense' && a.movement !== 0).sort((x, y) => y.movement - x.movement);
  const nothing = b.totals.debit === 0 && b.accounts.every((a) => a.opening === 0);
  if (nothing) return <EmptyState icon="book" title={t('acc.empty')} body={t('acc.empty_body')} />;
  return (
    <div className="rep-grid">
      <section className="rep-kpis" aria-label={t('acc.k_title')}>
        <Kpi label={t('acc.k_income')} value={money(pl.income)} hint={t('acc.k_income_hint')} />
        <Kpi label={t('acc.k_expenses')} value={money(pl.expenses)} hint={t('acc.k_expenses_hint')} />
        <Kpi label={pl.profit >= 0 ? t('acc.k_profit') : t('acc.k_loss')} value={money(pl.profit)} hint={pl.income ? t('acc.k_margin', { pct: Math.round((pl.profit / pl.income) * 100) }) : undefined} warn={pl.profit < 0} />
        <Kpi label={t('acc.k_vat')} value={money(b.vat.due)} hint={t('acc.k_vat_hint', { sales: money(b.vat.on_sales), purchases: money(b.vat.on_purchases) })} />
      </section>

      <section className="panel" aria-labelledby="acc-money">
        <div className="panel-head">
          <h2 id="acc-money">{t('acc.money')}</h2>
          <span className="muted">{t('acc.at', { date: day(b.to) })}</span>
        </div>
        <BalanceList rows={money_} onOpen={onOpen} />
      </section>

      <section className="panel" aria-labelledby="acc-owed">
        <div className="panel-head">
          <h2 id="acc-owed">{t('acc.owed')}</h2>
          <span className="muted">{t('acc.at', { date: day(b.to) })}</span>
        </div>
        <BalanceList rows={[...owed, ...extraOwed]} onOpen={onOpen} />
      </section>

      <section className="panel rep-wide" aria-labelledby="acc-costs">
        <div className="panel-head">
          <h2 id="acc-costs">{t('acc.costs')}</h2>
          <button className="link-button" onClick={() => onTab('statements', 'pl')}>
            {t('acc.see_pl')}
            <Icon name="chevronRight" size={16} />
          </button>
        </div>
        {expenses.length ? (
          <div className="shares">
            {expenses.slice(0, 8).map((a) => (
              <button key={a.id} className="share-item" onClick={() => onOpen(a)}>
                <span className="share-line">
                  <span className="share-name">
                    <span>
                      <span className="acc-code">{a.code}</span> {a.name}
                    </span>
                  </span>
                  <span className="share-value">{money(a.movement)}</span>
                </span>
                <span className="share-bar" aria-hidden="true">
                  <span style={{ width: `${pl.expenses > 0 ? Math.max(2, (a.movement / pl.expenses) * 100) : 0}%` }} />
                </span>
                <span className="share-foot muted">{pl.income > 0 ? t('acc.of_income', { pct: sharePct(a.movement, pl.income) }) : ''}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="muted">{t('acc.no_costs')}</p>
        )}
      </section>
    </div>
  );
}

function BalanceList({ rows, onOpen }: { rows: AccountRow[]; onOpen: (a: AccountRow) => void }) {
  return (
    <ul className="acc-balances">
      {rows.map((a) => (
        <li key={a.id}>
          <button onClick={() => onOpen(a)}>
            <span className="acc-code">{a.code}</span>
            <span className="acc-name">{a.name}</span>
            <span className={`acc-amount${a.closing < 0 ? ' negative' : ''}`}>{amount(a.closing)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------- Journal */

function JournalTab({ from, to, version, onOpen }: { from: string; to: string; version: number; onOpen: (id: string) => void }) {
  const [journal, setJournal] = useState<Journal | 'all'>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const input = { from, to, journal: journal === 'all' ? undefined : journal, search: q || undefined, v: version };
  const { data } = useAction<{ entries: EntryRow[]; more: boolean }>('accounting.journal', input);
  const fresh = data;
  return (
    <div className="rep-grid">
      <div className="toolbar acc-journal-tools">
        <div className="segmented" role="radiogroup" aria-label={t('acc.journal')}>
          {(['all', ...JOURNALS] as const).map((j) => (
            <button key={j} role="radio" aria-checked={journal === j} className={journal === j ? 'active' : ''} onClick={() => setJournal(j)}>
              {t(`acc.j_${j}`)}
            </button>
          ))}
        </div>
        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" placeholder={t('acc.search')} aria-label={t('acc.search')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setQ(search.trim())} />
          {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setSearch(''), setQ(''))} /> : null}
        </form>
      </div>
      <section className="panel rep-wide acc-journal">
        {!fresh ? (
          <Skeleton h={240} />
        ) : (
          <>
            <Table
              view="accounting.journal"
              head={[t('acc.c_date'), t('acc.c_no'), t('acc.c_journal'), t('acc.c_reference'), t('acc.c_memo'), t('acc.c_amount')]}
              phoneHide={[1, 2, 3]}
              left={[2, 3, 4]}
              empty={t('acc.no_entries')}
              rows={fresh.entries.map((e) => ({
                key: e.id,
                onClick: () => onOpen(e.id),
                cells: [
                  <span className="tabular">{day(e.date)}</span>,
                  <span className="tabular">{e.number}</span>,
                  <StatusBadge tone={JOURNAL_LOOK[e.journal].tone} icon={JOURNAL_LOOK[e.journal].icon} label={t(`acc.j_${e.journal}`)} />,
                  e.reference ?? '—',
                  <span className="acc-memo">
                    {e.memo}
                    {e.reversed_by ? <span className="badge badge-warn">{t('acc.reversed_by', { n: e.reversed_by })}</span> : null}
                  </span>,
                  <span className="tabular">{money(e.total)}</span>,
                ],
              }))}
            />
            {fresh.more ? <p className="muted acc-more">{t('acc.more')}</p> : null}
          </>
        )}
      </section>
    </div>
  );
}

function EntryDialog({ id, onClose, onOpen, onChanged }: { id: string; onClose: () => void; onOpen: (id: string) => void; onChanged: () => void }) {
  const { can } = useSession();
  const { data, reload } = useAction<EntryDetail>('accounting.entry', { entry: id });
  const [reversing, setReversing] = useState(false);
  const e = data?.entry;
  const debit = data?.lines.reduce((s, l) => s + l.debit, 0) ?? 0;
  const credit = data?.lines.reduce((s, l) => s + l.credit, 0) ?? 0;
  const canReverse = !!e && e.manual && !e.reversal_of && !e.reversed_by && can('accounting.post');
  return (
    <Dialog
      title={e ? t('acc.entry_title', { n: e.number }) : t('common.loading')}
      onClose={onClose}
      size="lg"
      footer={
        canReverse ? (
          <Button variant="secondary" icon="undo" onClick={() => setReversing(true)}>
            {t('acc.reverse')}
          </Button>
        ) : undefined
      }
    >
      {!data || !e ? (
        <Skeleton h={200} />
      ) : (
        <div className="acc-entry">
          <dl className="acc-meta">
            <div>
              <dt>{t('acc.c_date')}</dt>
              <dd>{day(e.date)}</dd>
            </div>
            <div>
              <dt>{t('acc.c_journal')}</dt>
              <dd>
                <StatusBadge tone={JOURNAL_LOOK[e.journal].tone} icon={JOURNAL_LOOK[e.journal].icon} label={t(`acc.j_${e.journal}`)} />
              </dd>
            </div>
            <div>
              <dt>{t('acc.c_reference')}</dt>
              <dd>{e.reference ?? '—'}</dd>
            </div>
            <div>
              <dt>{t('acc.posted')}</dt>
              <dd>{e.manual ? t('acc.posted_by', { name: e.by_name ?? '—', at: dateTime(e.created_at) }) : t('acc.posted_auto', { at: dateTime(e.created_at) })}</dd>
            </div>
          </dl>
          <p className="acc-entry-memo">{e.memo}</p>
          {e.reverses ? (
            <p className="callout callout-info">
              <Icon name="undo" size={16} />
              <span>
                {t('acc.reverses')}{' '}
                <button className="link-button" onClick={() => onOpen(e.reverses!.id)}>
                  {t('acc.entry_title', { n: e.reverses.number })}
                </button>
              </span>
            </p>
          ) : null}
          {e.reversed_by ? (
            <p className="callout callout-warn">
              <Icon name="undo" size={16} />
              <span>
                {t('acc.was_reversed')}{' '}
                <button className="link-button" onClick={() => onOpen(e.reversed_by!.id)}>
                  {t('acc.entry_title', { n: e.reversed_by.number })}
                </button>
              </span>
            </p>
          ) : null}
          <Table
            head={[t('acc.c_account'), t('acc.c_label'), t('acc.c_debit'), t('acc.c_credit')]}
            phoneHide={[1]}
            left={[1]}
            rows={data.lines.map((l) => ({
              key: l.id,
              cells: [
                <span>
                  <span className="acc-code">{l.code}</span> {l.account_name}
                </span>,
                [l.label, l.partner].filter(Boolean).join(' · ') || '—',
                <span className="tabular">{side(l.debit)}</span>,
                <span className="tabular">{side(l.credit)}</span>,
              ],
            }))}
            foot={[t('acc.total'), '', <span className="tabular strong">{money(debit)}</span>, <span className="tabular strong">{money(credit)}</span>]}
          />
          {!e.manual ? <p className="muted small">{t('acc.auto_hint')}</p> : null}
        </div>
      )}
      {reversing && e ? (
        <ReverseDialog
          entry={e}
          onClose={() => setReversing(false)}
          onDone={(newId) => {
            setReversing(false);
            void reload();
            onChanged();
            onOpen(newId);
          }}
        />
      ) : null}
    </Dialog>
  );
}

function ReverseDialog({ entry, onClose, onDone }: { entry: EntryRow; onClose: () => void; onDone: (id: string) => void }) {
  const { fail, toast } = useUi();
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await action<{ id: string; number: number }>('accounting.reverse', { entry: entry.id, reason, date });
      toast(t('acc.reversed', { n: entry.number, m: r.number }), 'ok');
      onDone(r.id);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('acc.reverse_title', { n: entry.number })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="undo" busy={busy} disabled={reason.trim().length < 3} onClick={go}>
            {t('acc.reverse')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('acc.reverse_hint')}</p>
      <Field label={t('acc.reverse_reason')}>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('acc.reverse_example')} data-autofocus />
      </Field>
      <Field label={t('acc.c_date')}>
        <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- New entry */

interface FormLine {
  key: number;
  account: string;
  label: string;
  debit: string;
  credit: string;
}
/** "1,250.50" or "1250,5" → 125050 minor units; null when it is not a number. */
function parseAmount(s: string): number | null {
  const clean = s.replace(/\s/g, '');
  if (!clean) return 0;
  const normal = /,\d{1,2}$/.test(clean) && !clean.includes('.') ? clean.replace(',', '.') : clean.replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(normal)) return null;
  return Math.round(Number(normal) * 100);
}

/** A share of income people can read: "0.3" rather than a misleading "0". */
const sharePct = (part: number, whole: number) => {
  const p = (part / whole) * 100;
  return Math.abs(p) >= 10 || p === 0 ? String(Math.round(p)) : p.toFixed(1);
};

/** 125050 → "1250.50", 18000000 → "180000": the way people type amounts. */
const amountText = (minor: number) => (minor % 100 ? (minor / 100).toFixed(2) : String(minor / 100));

function EntryForm({ accounts, lockDate, onClose, onDone }: { accounts: AccountRow[]; lockDate: string | null; onClose: () => void; onDone: (id: string) => void }) {
  const { fail, toast } = useUi();
  const [date, setDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [reference, setReference] = useState('');
  const [next, setNext] = useState(3);
  const [lines, setLines] = useState<FormLine[]>([
    { key: 1, account: '', label: '', debit: '', credit: '' },
    { key: 2, account: '', label: '', debit: '', credit: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const usable = useMemo(() => accounts.filter((a) => a.active), [accounts]);
  const parsed = lines.map((l) => ({ ...l, d: parseAmount(l.debit), c: parseAmount(l.credit) }));
  const debit = parsed.reduce((s, l) => s + (l.d ?? 0), 0);
  const credit = parsed.reduce((s, l) => s + (l.c ?? 0), 0);
  const filled = parsed.filter((l) => l.account || l.d || l.c);
  const problems = [
    memo.trim().length < 3 ? t('acc.f_need_memo') : null,
    filled.length < 2 ? t('acc.f_need_two') : null,
    filled.some((l) => !l.account) ? t('acc.f_need_account') : null,
    parsed.some((l) => l.d === null || l.c === null) ? t('acc.f_bad_amount') : null,
    filled.some((l) => !!l.d === !!l.c) ? t('acc.f_one_side') : null,
    lockDate && date <= lockDate ? t('acc.f_closed', { date: day(lockDate) }) : null,
  ].filter((x): x is string => !!x);
  const balanced = debit === credit && debit > 0;
  const setLine = (key: number, patch: Partial<FormLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const balanceLine = (key: number) => {
    const others = parsed.filter((l) => l.key !== key);
    const d = others.reduce((s, l) => s + (l.d ?? 0), 0);
    const c = others.reduce((s, l) => s + (l.c ?? 0), 0);
    if (d === c) return;
    setLine(key, d > c ? { credit: amountText(d - c), debit: '' } : { debit: amountText(c - d), credit: '' });
  };
  const post = async () => {
    setBusy(true);
    try {
      const e = await action<{ id: string; number: number }>('accounting.post', {
        date,
        memo,
        reference: reference || null,
        lines: filled.map((l) => ({ account: l.account, debit: l.d ?? 0, credit: l.c ?? 0, label: l.label || null })),
      });
      toast(t('acc.posted_ok', { n: e.number }), 'ok');
      onDone(e.id);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('acc.new_entry')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className={`acc-balance-state ${balanced ? 'ok' : 'off'}`} role="status">
            <Icon name={balanced ? 'checkCircle' : 'alert'} size={16} />
            {balanced ? t('acc.f_balanced', { total: money(debit) }) : t('acc.f_off', { diff: money(Math.abs(debit - credit)) })}
          </span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!balanced || problems.length > 0} onClick={post}>
            {t('acc.post')}
          </Button>
        </>
      }
    >
      <div className="form-grid three">
        <Field label={t('acc.c_date')}>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('acc.c_reference')} hint={t('acc.f_reference_hint')}>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label={t('acc.f_memo')}>
          <input className="input" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder={t('acc.f_memo_example')} data-autofocus />
        </Field>
      </div>
      <div className="acc-lines" role="group" aria-label={t('acc.f_lines')}>
        <div className="acc-line acc-line-head" aria-hidden="true">
          <span>{t('acc.c_account')}</span>
          <span>{t('acc.c_label')}</span>
          <span>{t('acc.c_debit')}</span>
          <span>{t('acc.c_credit')}</span>
          <span />
        </div>
        {lines.map((l, i) => {
          const p = parsed[i]!;
          return (
            <div key={l.key} className="acc-line">
              <select className="input" aria-label={t('acc.f_account_n', { n: i + 1 })} value={l.account} onChange={(e) => setLine(l.key, { account: e.target.value })}>
                <option value="">{t('acc.f_choose')}</option>
                {TYPES.map((ty) => (
                  <optgroup key={ty} label={t(`acc.t_${ty}`)}>
                    {usable
                      .filter((a) => a.type === ty)
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
              <input className="input" aria-label={t('acc.f_label_n', { n: i + 1 })} placeholder={t('acc.c_label')} value={l.label} onChange={(e) => setLine(l.key, { label: e.target.value })} />
              <input
                className={`input tabular${p.d === null ? ' invalid' : ''}`}
                inputMode="decimal"
                aria-label={t('acc.f_debit_n', { n: i + 1 })}
                placeholder={t('acc.c_debit')}
                value={l.debit}
                onChange={(e) => setLine(l.key, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                onFocus={() => !l.debit && !l.credit && balanceLine(l.key)}
              />
              <input
                className={`input tabular${p.c === null ? ' invalid' : ''}`}
                inputMode="decimal"
                aria-label={t('acc.f_credit_n', { n: i + 1 })}
                placeholder={t('acc.c_credit')}
                value={l.credit}
                onChange={(e) => setLine(l.key, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
              />
              <IconButton icon="trash" label={t('acc.f_remove')} disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} />
            </div>
          );
        })}
        <Button
          variant="ghost"
          icon="plus"
          onClick={() => {
            setLines((ls) => [...ls, { key: next, account: '', label: '', debit: '', credit: '' }]);
            setNext((n) => n + 1);
          }}
        >
          {t('acc.f_add_line')}
        </Button>
      </div>
      {problems.length && filled.length ? (
        <ul className="acc-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : (
        <p className="muted small">{t('acc.f_hint')}</p>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Accounts */

function AccountsTab({ books: b, onOpen, onChanged }: { books: Books; onOpen: (a: AccountRow) => void; onChanged: () => void }) {
  const { can } = useSession();
  const [editing, setEditing] = useState<AccountRow | 'new' | null>(null);
  const [showUnused, setShowUnused] = useState(false);
  const rows = b.accounts.filter((a) => showUnused || a.used || a.key || a.closing !== 0);
  const hidden = b.accounts.length - rows.length;
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <p className="muted">{t('acc.accounts_hint', { date: day(b.to) })}</p>
        <div className="toolbar-group">
          {hidden || showUnused ? (
            <Button variant="ghost" onClick={() => setShowUnused((v) => !v)}>
              {showUnused ? t('acc.hide_unused') : tn('acc.show_unused', hidden)}
            </Button>
          ) : null}
          {can('accounting.settings') ? (
            <Button variant="secondary" icon="plus" onClick={() => setEditing('new')}>
              {t('acc.new_account')}
            </Button>
          ) : null}
        </div>
      </div>
      {TYPES.map((ty) => {
        const list = rows.filter((a) => a.type === ty);
        if (!list.length) return null;
        const total = list.reduce((s, a) => s + a.closing, 0);
        return (
          <section key={ty} className="panel rep-wide acc-type" aria-labelledby={`acc-t-${ty}`}>
            <div className="panel-head">
              <h2 id={`acc-t-${ty}`}>{t(`acc.t_${ty}s`)}</h2>
              <span className="muted">{t(`acc.t_${ty}_hint`)}</span>
            </div>
            <Table
              head={[t('acc.c_code'), t('acc.c_name'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance'), '']}
              phoneHide={[2, 3]}
              left={[1]}
              rows={list.map((a) => ({
                key: a.id,
                onClick: () => onOpen(a),
                cells: [
                  <span className="acc-code">{a.code}</span>,
                  <span className="acc-name-cell">
                    {a.name}
                    {a.key ? <span className="badge badge-neutral" title={t('acc.auto_account_hint')}>{t('acc.auto_account')}</span> : null}
                    {!a.active ? <span className="badge badge-warn">{t('acc.off')}</span> : null}
                  </span>,
                  <span className="tabular">{side(a.debit)}</span>,
                  <span className="tabular">{side(a.credit)}</span>,
                  <span className={`tabular strong${a.closing < 0 ? ' negative' : ''}`}>{amount(a.closing)}</span>,
                  can('accounting.settings') ? (
                    <IconButton icon="edit" label={t('acc.edit_account', { name: a.name })} onClick={(e) => (e.stopPropagation(), setEditing(a))} />
                  ) : null,
                ],
              }))}
              foot={[t('acc.total'), '', '', '', <span className="tabular strong">{amount(total)}</span>, '']}
            />
          </section>
        );
      })}
      {editing ? <AccountForm account={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onDone={() => (setEditing(null), onChanged())} /> : null}
    </div>
  );
}

function AccountForm({ account, onClose, onDone }: { account: AccountRow | null; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const [code, setCode] = useState(account?.code ?? '');
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState<AccountType>(account?.type ?? 'expense');
  const [active, setActive] = useState(account?.active ?? true);
  const [flow, setFlow] = useState<CashFlowKind | ''>(account?.cash_flow_set ? (account.cash_flow ?? '') : '');
  const system = !!account?.key;
  const save = async () => {
    const ok = await run(
      () => action('accounting.save_account', { account: account?.id, code: code.trim(), name: name.trim(), type, active, cash_flow: flow || null }),
      account ? t('acc.account_saved', { name }) : t('acc.account_added', { name }),
    );
    if (ok) onDone();
  };
  return (
    <Dialog
      title={account ? t('acc.edit_account', { name: account.name }) : t('acc.new_account')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!code.trim() || name.trim().length < 2} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {system ? <p className="callout callout-info">{t('acc.system_account')}</p> : null}
      <div className="form-grid">
        <Field label={t('acc.c_code')} hint={t('acc.code_hint')}>
          <input className="input tabular" value={code} onChange={(e) => setCode(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('acc.c_type')}>
          <select className="input" value={type} disabled={system || !!account?.used} onChange={(e) => setType(e.target.value as AccountType)}>
            {TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`acc.t_${ty}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t('acc.c_name')}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t('cf.kind')} hint={t('cf.kind_hint')}>
        <select className="input" value={flow} onChange={(e) => setFlow(e.target.value as CashFlowKind | '')}>
          <option value="">{account?.cash_flow && !account.cash_flow_set ? t('cf.k_auto_is', { kind: t(`cf.k_${account.cash_flow}`) }) : t('cf.k_auto')}</option>
          {CASH_FLOWS.map((k) => (
            <option key={k} value={k}>
              {t(`cf.k_${k}`)}
            </option>
          ))}
        </select>
      </Field>
      {!system ? (
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>
            <span className="strong">{t('acc.in_use')}</span>
            <span className="muted">{t('acc.in_use_hint')}</span>
          </span>
        </label>
      ) : null}
    </Dialog>
  );
}

function LedgerDialog({ account, from, to, onClose, onEntry }: { account: AccountRow; from: string; to: string; onClose: () => void; onEntry: (id: string) => void }) {
  const { data } = useAction<Ledger>('accounting.ledger', { account: account.id, from, to });
  const download = () =>
    data &&
    downloadCsv(
      `ledger-${account.code}-${from}-${to}.csv`,
      [t('acc.c_date'), t('acc.c_no'), t('acc.c_journal'), t('acc.c_reference'), t('acc.c_memo'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')],
      [
        ['', '', '', '', t('acc.opening'), '', '', plain(data.opening)],
        ...data.lines.map((l) => [l.date, l.number, t(`acc.j_${l.journal}`), l.reference ?? '', [l.memo, l.label].filter(Boolean).join(' · '), plain(l.debit), plain(l.credit), plain(l.balance)]),
      ],
    );
  return (
    <Dialog
      title={`${account.code} ${account.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="secondary" icon="download" disabled={!data} onClick={download}>
          {t('rep.download')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <div className="acc-ledger">
          <dl className="fiscal-stats">
            <div className="fiscal-stat">
              <dt>{t('acc.opening_at', { date: day(from) })}</dt>
              <dd>{money(data.opening)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.c_debit')}</dt>
              <dd>{money(data.debit)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.c_credit')}</dt>
              <dd>{money(data.credit)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('acc.closing_at', { date: day(to) })}</dt>
              <dd>{money(data.closing)}</dd>
            </div>
          </dl>
          <Table
            head={[t('acc.c_date'), t('acc.c_memo'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')]}
            sortable={false}
            phoneHide={[]}
            left={[1]}
            empty={t('acc.no_lines')}
            rows={data.lines.map((l) => ({
              key: l.id,
              onClick: () => onEntry(l.entry),
              cells: [
                day(l.date),
                <span className="acc-memo">
                  {l.memo}
                  <span className="muted small">{[t('acc.entry_title', { n: l.number }), l.reference, l.label !== l.memo ? l.label : null].filter(Boolean).join(' · ')}</span>
                </span>,
                <span className="tabular">{side(l.debit)}</span>,
                <span className="tabular">{side(l.credit)}</span>,
                <span className={`tabular strong${l.balance < 0 ? ' negative' : ''}`}>{money(l.balance)}</span>,
              ],
            }))}
          />
          {data.more ? <p className="muted acc-more">{t('acc.more_lines')}</p> : null}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Statements */

function Statements({
  books: b,
  version,
  which,
  onWhich,
  onOpen,
  onEntry,
}: {
  books: Books;
  version: number;
  which: Statement;
  onWhich: (s: Statement) => void;
  onOpen: (a: AccountRow) => void;
  onEntry: (id: string) => void;
}) {
  const period = `${b.from}-${b.to}`;
  const pl = b.profit_loss;
  const bs = b.balance_sheet;
  const income = b.accounts.filter((a) => a.type === 'income' && (a.movement !== 0 || a.used));
  const expenses = b.accounts.filter((a) => a.type === 'expense' && a.movement !== 0);
  const closing = (ty: AccountType) => b.accounts.filter((a) => a.type === ty && a.closing !== 0);
  const tb = b.accounts.filter((a) => a.opening !== 0 || a.debit !== 0 || a.credit !== 0);
  const accountCell = (a: AccountRow) => (
    <span>
      <span className="acc-code">{a.code}</span> {a.name}
    </span>
  );
  const csv = (name: string, rows: (string | number)[][], head = [t('acc.c_code'), t('acc.c_name'), t('acc.c_amount')]) => () => downloadCsv(`${name}-${period}.csv`, head, rows);

  let body: ReactNode;
  if (which === 'pl') {
    body = (
      <section className="panel rep-wide statement">
        <div className="panel-head">
          <h2>{t('acc.s_pl')}</h2>
          <Button variant="ghost" icon="download" onClick={csv('profit-and-loss', [...income, ...expenses].map((a) => [a.code, a.name, plain(a.movement)]))}>
            {t('rep.download')}
          </Button>
        </div>
        <StatementGroup title={t('acc.t_incomes')} rows={income} value={(a) => a.movement} total={pl.income} cell={accountCell} onOpen={onOpen} />
        <StatementGroup title={t('acc.t_expenses')} rows={expenses} value={(a) => a.movement} total={pl.expenses} cell={accountCell} onOpen={onOpen} />
        <div className={`statement-result${pl.profit < 0 ? ' negative' : ''}`}>
          <span>{pl.profit >= 0 ? t('acc.k_profit') : t('acc.k_loss')}</span>
          <span className="tabular">{money(pl.profit)}</span>
        </div>
      </section>
    );
  } else if (which === 'bs') {
    const balanced = bs.assets === bs.liabilities + bs.equity + bs.earnings;
    body = (
      <section className="panel rep-wide statement">
        <div className="panel-head">
          <h2>{t('acc.s_bs_at', { date: day(b.to) })}</h2>
          <Button variant="ghost" icon="download" onClick={csv('balance-sheet', [...closing('asset'), ...closing('liability'), ...closing('equity')].map((a) => [a.code, a.name, plain(a.closing)]).concat([['', t('acc.earnings'), plain(bs.earnings)]]))}>
            {t('rep.download')}
          </Button>
        </div>
        <div className="statement-cols">
          <div>
            <StatementGroup title={t('acc.t_assets')} rows={closing('asset')} value={(a) => a.closing} total={bs.assets} cell={accountCell} onOpen={onOpen} />
          </div>
          <div>
            <StatementGroup title={t('acc.t_liabilitys')} rows={closing('liability')} value={(a) => a.closing} total={bs.liabilities} cell={accountCell} onOpen={onOpen} />
            <StatementGroup
              title={t('acc.t_equitys')}
              rows={closing('equity')}
              value={(a) => a.closing}
              extra={{ label: t('acc.earnings'), value: bs.earnings }}
              total={bs.equity + bs.earnings}
              cell={accountCell}
              onOpen={onOpen}
            />
            <div className="statement-result">
              <span>{t('acc.liab_equity')}</span>
              <span className="tabular">{money(bs.liabilities + bs.equity + bs.earnings)}</span>
            </div>
          </div>
        </div>
        <p className={`acc-check ${balanced ? 'ok' : 'off'}`}>
          <Icon name={balanced ? 'checkCircle' : 'alert'} size={16} />
          {balanced ? t('acc.bs_ok') : t('acc.bs_off')}
        </p>
      </section>
    );
  } else if (which === 'cf') {
    body = <CashFlowStatement from={b.from} to={b.to} version={version} onAccount={(id) => b.accounts.some((a) => a.id === id) && onOpen(b.accounts.find((a) => a.id === id)!)} />;
  } else if (which === 'partners') {
    body = <PartnerLedger from={b.from} to={b.to} version={version} onEntry={onEntry} />;
  } else if (which === 'vat') {
    body = (
      <section className="panel rep-wide statement">
        <div className="panel-head">
          <h2>{t('acc.s_vat')}</h2>
          <Button variant="ghost" icon="download" onClick={csv('vat', [[t('acc.vat_sales'), plain(b.vat.on_sales)], [t('acc.vat_purchases'), plain(b.vat.on_purchases)], [b.vat.due >= 0 ? t('acc.vat_due') : t('acc.vat_back'), plain(b.vat.due)]], [t('acc.c_name'), t('acc.c_amount')])}>
            {t('rep.download')}
          </Button>
        </div>
        <dl className="vat-sheet">
          <div>
            <dt>{t('acc.vat_sales')}</dt>
            <dd className="tabular">{money(b.vat.on_sales)}</dd>
          </div>
          <div>
            <dt>{t('acc.vat_purchases')}</dt>
            <dd className="tabular">− {money(b.vat.on_purchases)}</dd>
          </div>
          <div className="statement-result">
            <dt>{b.vat.due >= 0 ? t('acc.vat_due') : t('acc.vat_back')}</dt>
            <dd className="tabular">{money(Math.abs(b.vat.due))}</dd>
          </div>
        </dl>
        <p className="muted small">{t('acc.vat_hint')}</p>
      </section>
    );
  } else {
    body = (
      <section className="panel rep-wide statement">
        <div className="panel-head">
          <h2>{t('acc.s_tb')}</h2>
          <Button
            variant="ghost"
            icon="download"
            onClick={csv(
              'trial-balance',
              tb.map((a) => [a.code, a.name, plain(a.opening), plain(a.debit), plain(a.credit), plain(a.closing)]),
              [t('acc.c_code'), t('acc.c_name'), t('acc.opening'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')],
            )}
          >
            {t('rep.download')}
          </Button>
        </div>
        <Table
          head={[t('acc.c_account'), t('acc.opening'), t('acc.c_debit'), t('acc.c_credit'), t('acc.c_balance')]}
          phoneHide={[1]}
          empty={t('acc.no_entries')}
          rows={tb.map((a) => ({
            key: a.id,
            onClick: () => onOpen(a),
            cells: [accountCell(a), <span className="tabular">{amount(a.opening)}</span>, <span className="tabular">{side(a.debit)}</span>, <span className="tabular">{side(a.credit)}</span>, <span className="tabular strong">{amount(a.closing)}</span>],
          }))}
          foot={[t('acc.total'), '', <span className="tabular strong">{money(b.totals.debit)}</span>, <span className="tabular strong">{money(b.totals.credit)}</span>, '']}
        />
        <p className={`acc-check ${b.totals.debit === b.totals.credit ? 'ok' : 'off'}`}>
          <Icon name={b.totals.debit === b.totals.credit ? 'checkCircle' : 'alert'} size={16} />
          {b.totals.debit === b.totals.credit ? t('acc.tb_ok') : t('acc.tb_off')}
        </p>
      </section>
    );
  }
  return (
    <div className="rep-grid">
      <div className="segmented statement-tabs" role="radiogroup" aria-label={t('acc.tab_statements')}>
        {STATEMENTS.map((s) => (
          <button key={s} role="radio" aria-checked={which === s} className={which === s ? 'active' : ''} onClick={() => onWhich(s)}>
            {t(`acc.s_${s}` as MessageKey)}
          </button>
        ))}
      </div>
      {body}
    </div>
  );
}

function StatementGroup({
  title,
  rows,
  value,
  total,
  extra,
  cell,
  onOpen,
}: {
  title: string;
  rows: AccountRow[];
  value: (a: AccountRow) => number;
  total: number;
  extra?: { label: string; value: number };
  cell: (a: AccountRow) => ReactNode;
  onOpen: (a: AccountRow) => void;
}) {
  return (
    <div className="statement-group">
      <h3>{title}</h3>
      <ul>
        {rows.map((a) => (
          <li key={a.id}>
            <button onClick={() => onOpen(a)}>
              {cell(a)}
              <span className={`tabular${value(a) < 0 ? ' negative' : ''}`}>{money(value(a))}</span>
            </button>
          </li>
        ))}
        {extra ? (
          <li>
            <span className="statement-extra">
              <span>{extra.label}</span>
              <span className={`tabular${extra.value < 0 ? ' negative' : ''}`}>{money(extra.value)}</span>
            </span>
          </li>
        ) : null}
        {!rows.length && !extra ? <li className="muted">—</li> : null}
      </ul>
      <div className="statement-total">
        <span>{t('acc.total')}</span>
        <span className="tabular">{money(total)}</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Closing the books */

function CloseBooks({ current, onClose, onDone }: { current: string | null; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const today = todayIso();
  const lastMonthEnd = (() => {
    const d = new Date(`${today.slice(0, 8)}01T00:00:00Z`);
    d.setUTCDate(0);
    return d.toISOString().slice(0, 10);
  })();
  const [date, setDate] = useState(current ?? lastMonthEnd);
  const save = async (value: string | null) => {
    const ok = await run(() => action('accounting.set_lock_date', { lock_date: value }), value ? t('acc.closed_ok', { date: day(value) }) : t('acc.reopened'));
    if (ok) onDone();
  };
  return (
    <Dialog
      title={t('acc.close_books')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {current ? (
            <Button variant="ghost" onClick={() => void save(null)}>
              {t('acc.reopen')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          )}
          <Button variant="primary" icon="lock" busy={busy} disabled={!isDay(date) || date >= today} onClick={() => void save(date)}>
            {t('acc.close_to', { date: isDay(date) ? day(date) : '…' })}
          </Button>
        </>
      }
    >
      <p className="muted">{t('acc.close_hint')}</p>
      <Field label={t('acc.close_date')} hint={current ? t('acc.close_current', { date: day(current) }) : undefined}>
        <input className="input" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} data-autofocus />
      </Field>
      <p className="muted small">{t('acc.reopen_hint')}</p>
    </Dialog>
  );
}
